/**
 * The admin's usage card on a founder's page (board usage-r1, "What we see"): the last 7 days against the 7 before,
 * the stuck and idle signals, and each Linux user, never below the hour. From GET /api/admin/usage.
 */
import { ago } from '../../domain/format'
import type { Api } from '../../infra/api'
import { adminWeekWords, AGENT_NAME, shortTime } from '../../domain/usage'
import { Card, Chip, DataTable, EmptyState, ErrorNote, Inline, Skeleton, Stack, Text } from '../../ui'
import { useLoad } from '../useLoad'

export function UsageAdminCard({ api, founderId, now }: { api: Api; founderId: string; now: number }) {
  const u = useLoad(() => api.adminUsage(), [api])
  const row = u.data?.founders.find((f) => f.founder.id === founderId)
  return (
    <Card title="Coding usage">
      {u.error && !u.data ? (
        <ErrorNote reason="Usage didn't load." onRetry={u.reload} />
      ) : !u.data ? (
        <Skeleton height="block" />
      ) : !row ? (
        <EmptyState title="No usage reported yet.">Boxes count from agent 0.5.0, every 15 minutes.</EmptyState>
      ) : (
        <Stack gap="md">
          <Stack gap="xs">
            <Text weight="head">{adminWeekWords(row)}</Text>
            <Text size="sm" tone="muted">
              {[
                row.week.lastActiveAt ? `Last active ${ago(row.week.lastActiveAt, now)}` : 'Never active',
                row.week.agents.length ? row.week.agents.map((a) => AGENT_NAME[a]).join(', ') : 'no agent this week',
                row.limitHits ? `${row.limitHits} limit run-out${row.limitHits === 1 ? '' : 's'} this week` : null,
              ].filter(Boolean).join(' · ')}
            </Text>
          </Stack>
          {row.signals.length > 0 && (
            <Inline gap="xs">
              {row.signals.map((s) => (
                <Chip key={s.key} tone="look" light="look">{s.text}</Chip>
              ))}
            </Inline>
          )}
          <DataTable
            label="Usage per Linux user"
            framed={false}
            rows={row.users}
            rowKey={(r) => r.linuxUser}
            empty={<EmptyState title="Nobody yet.">Each person on the box shows once their agent counts something.</EmptyState>}
            columns={[
              { key: 'user', label: 'Linux user', narrow: 'main', render: (r) => <Text mono weight="head">{r.linuxUser}</Text> },
              { key: 'time', label: '7 days', narrow: 'end', render: (r) => <Text mono size="sm">{shortTime(r.activeMinutes)}</Text> },
              { key: 'sessions', label: 'Sessions', render: (r) => <Text mono size="sm">{String(r.sessions)}</Text> },
              { key: 'commits', label: 'Commits', render: (r) => <Text mono size="sm">{String(r.commits)}</Text> },
              { key: 'agents', label: 'Agents', render: (r) => <Text size="sm">{r.agents.map((a) => AGENT_NAME[a]).join(', ') || '–'}</Text> },
              { key: 'seen', label: 'Last active', render: (r) => <Text size="sm" tone="muted">{r.lastActiveAt ? ago(r.lastActiveAt, now) : 'never'}</Text> },
            ]}
          />
        </Stack>
      )}
    </Card>
  )
}
